"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ownsTenantId = exports.flattenTenants = exports.isSubTenant = exports.getPath = exports.isEqual = exports.Tenant = exports.findFosterTenant = exports.AmbiguousFosterTenantError = exports.shouldAdopt = exports.Association = exports.Id = exports.ROOT_TENANT_ID = exports.NAMESPACE = void 0;
const effect_1 = require("effect");
const Function_1 = require("effect/Function");
const entity_1 = require("../entity");
const geography_1 = require("../geography");
const net_1 = require("../net");
const base_1 = require("./business-unit/base");
const common_1 = require("./device/class/common");
exports.NAMESPACE = 'Culligan.Tenant';
exports.ROOT_TENANT_ID = 'root';
exports.Id = effect_1.Schema.NonEmptyString.annotations({
    identifier: `${exports.NAMESPACE}.Id`,
});
exports.Association = effect_1.Schema.Struct({
    countryCode: effect_1.Schema.optionalWith(geography_1.CountryCodeIso3166Alpha2, { exact: true, as: 'Option' }).pipe(effect_1.Schema.withConstructorDefault(effect_1.Option.none)),
    modelCode: effect_1.Schema.optionalWith(common_1.ModelCode, { exact: true, as: 'Option' }).pipe(effect_1.Schema.withConstructorDefault(effect_1.Option.none)),
})
    .pipe(
/** An association is only meaningful and valid if it contains at least one matcher */
effect_1.Schema.filter((0, effect_1.flow)(effect_1.Record.getSomes, effect_1.Record.isEmptyReadonlyRecord, effect_1.Boolean.not)))
    .annotations({
    identifier: `${exports.NAMESPACE}.Association`,
    arbitrary: () => FC => FC.record({
        countryCode: FC.oneof(effect_1.Arbitrary.make(geography_1.CountryCodeIso3166Alpha2), FC.constant(null)),
        modelCode: FC.oneof(effect_1.Arbitrary.make(common_1.ModelCode), FC.constant(null)),
    })
        .map(({ countryCode, modelCode }) => ({
        countryCode: effect_1.Option.fromNullable(countryCode),
        modelCode: effect_1.Option.fromNullable(modelCode),
    }))
        .filter((0, effect_1.flow)(effect_1.Record.getSomes, effect_1.Record.isEmptyReadonlyRecord, effect_1.Boolean.not)),
});
function matchesAssociation(matcher, value) {
    return effect_1.Option.match(matcher, {
        onNone: Function_1.constTrue,
        onSome: matcher => value.pipe(effect_1.Option.filter(effect_1.Equal.equals(matcher)), effect_1.Option.map(Function_1.constTrue), effect_1.Option.getOrElse(Function_1.constFalse)),
    });
}
/**
 * See {@link Association}
 *
 * Utility function to determine whether the provided `tenant` should
 * adopt entities matching the provided `options`.
 *
 * @param self The prospective foster {@link Tenant}
 * @param options The entity options to check against
 */
exports.shouldAdopt = effect_1.Function.dual(2, (tenant, options) => tenant.associations.some(association => effect_1.Record.keys(exports.Association.from.fields).every(key => matchesAssociation(association[key], options[key]))));
class AmbiguousFosterTenantError extends effect_1.Data.TaggedError('AmbiguousFosterTenantError') {
    get message() {
        return ('Multiple tenants were found when trying to find ' +
            'a foster tenant for entity specifying ' +
            JSON.stringify(effect_1.Record.getSomes(this.options)) +
            ':\n\n' +
            this.tenants
                .map(({ id, name, associations }) => ` - ${name} (${id}):\n` +
                `${associations.map(association => '  - ' + JSON.stringify(effect_1.Record.getSomes(association))).join('\n')}`)
                .join('\n\n'));
    }
}
exports.AmbiguousFosterTenantError = AmbiguousFosterTenantError;
/**
 * Attempts to find the singular foster {@link Tenant} for an entity based on
 * the {@link Association}s specified on the tenant and the provided options.
 *
 * If no foster tenants are found, a {@link Cause.NoSuchElementException NoSuchElementException} error is returned.
 *
 * If multiple foster tenants are found, an {@link AmbiguousFosterTenantError} error is returned.
 */
exports.findFosterTenant = effect_1.Effect.fn('findFosterTenant')(function* (tenant, options) {
    const tenants = (0, effect_1.pipe)(tenant, (0, exports.flattenTenants)(true), effect_1.Array.map(effect_1.Option.liftPredicate((0, exports.shouldAdopt)(options))), effect_1.Array.getSomes);
    return yield* tenants.length > 1
        ? new AmbiguousFosterTenantError({ options, tenants })
        : effect_1.Array.head(tenants);
});
const fields = {
    ...entity_1.BaseTimestampedEntity.fields,
    id: exports.Id,
    name: effect_1.Schema.NonEmptyString,
    associations: effect_1.Schema.optionalWith(effect_1.Schema.Array(exports.Association), { exact: true, default: () => [] }),
    businessUnit: effect_1.Schema.optionalWith(base_1.BusinessUnit, { exact: true, as: 'Option' }),
    homeUrl: effect_1.Schema.optionalWith(effect_1.Schema.NonEmptyString, { exact: true, as: 'Option' }),
    emailFromAddress: effect_1.Schema.optionalWith(net_1.Email, { exact: true, nullable: true, as: 'Option' }),
    customDomainFrontend: effect_1.Schema.optionalWith(net_1.Hostname, { exact: true, nullable: true, as: 'Option' }),
};
exports.Tenant = effect_1.Schema.suspend(() => effect_1.Schema.Struct({
    ...fields,
    subTenants: effect_1.Schema.Array(exports.Tenant),
})).annotations({
    identifier: `${exports.NAMESPACE}.Tenant`,
    description: 'A Tenant.',
    equivalence: () => (self, that) => self.id === that.id,
});
exports.isEqual = effect_1.Schema.equivalence(exports.Tenant);
/**
 * See {@link Path}
 *
 * @returns an {@link Option.Option Option}-wrapped {@link Path} (`fromTenant` -> `toTenant`, inclusive) between the tenants.
 */
const getPath = (fromTenant, toTenant, currentPath = []) => fromTenant.id === toTenant.id
    ? effect_1.Option.some([...currentPath, fromTenant])
    : effect_1.Array.reduce(fromTenant.subTenants, effect_1.Option.none(), (acc, subTenant) => effect_1.Option.isSome(acc) ? acc : (0, exports.getPath)(subTenant, toTenant, [...currentPath, fromTenant]));
exports.getPath = getPath;
/**
 * Utility function to check whether `tenant` is an (in)direct sub-tenant of `superTenant`.
 *
 * @returns `false` if `superTenant` is equal to `tenant`.
 * @returns `false` if `tenant` is not an (in)direct {@link Tenant subTenant} of `superTenant`.
 * @returns `true` if `tenant` is an (in)direct {@link Tenant subTenant} of `superTenant`.
 */
exports.isSubTenant = effect_1.Function.dual(2, (superTenant, tenant) => (0, exports.getPath)(superTenant, tenant).pipe(effect_1.Option.filter(path => path.length > 1), effect_1.Option.isSome));
/**
 * Rearranges a tenant tree (where tenant(s) may have zero to many sub-tenants)
 * into a flat list of tenants.
 *
 * @param tenant The top-level tenant to start resolving sub-tenants from
 * @param retainSubTenants Whether each tenants' `subTenants` should be left as-is (emptied by default)
 */
exports.flattenTenants = effect_1.Function.dual(args => args[0] !== undefined && typeof args[0] !== 'boolean', (tenant, retainSubTenants = false) => [
    { ...tenant, subTenants: retainSubTenants ? tenant.subTenants : [] },
    ...tenant.subTenants.flatMap((0, exports.flattenTenants)(retainSubTenants)),
]);
/**
 * Whether the given tenant or any of its children have the given Tenant ID.
 */
const ownsTenantId = (tenant, id) => (0, exports.flattenTenants)(tenant).some(t => t.id === id);
exports.ownsTenantId = ownsTenantId;
//# sourceMappingURL=tenant.js.map